// The reply box.
//
// `is_internal_note` is required by POST /api/tickets/:publicRef/messages and has no
// default (docs/API.md §3.5) precisely because a wrong guess is the failure mode that
// matters. So the mode is not a checkbox tucked beside the send button: it is two
// explicit choices, and picking one restyles the entire composer — border, background,
// banner, and the wording on the button that sends it.
//
// The composer opens on "Internal note". Both directions of mistake are possible, but
// only one of them is unrecoverable: a note that should have gone to the customer can
// be sent a second time; a note that should not have gone cannot be recalled.
//
// Nothing here is authorization. The server checks `can_send_customer_email` on every
// outbound message and so does the database (SPEC.md §3.3, docs/API.md §1.8.2); the
// disabled control exists so an agent finds out before typing, not after.

import { useRef, useState } from 'react';

import { api } from '../../api/client.js';
import { useSession } from '../../session/SessionProvider.jsx';
import { BASE_STATUSES, STATUS_LABELS } from './format.js';

// Both modes are built from the semantic sets in src/styles/app.css — the warm note set
// for internal, the cool reply set for customer-facing — so the two never converge on a
// pair of similar greys in dark mode (app.css RULE 3, SPEC.md §7.4).
//
// Internal mode is a four-step amber hierarchy that reads at a glance in either theme:
//
//   panel   bg-note          the whole composer is tinted, with a 2px amber-500 edge
//   selected bg-note-strong  the chosen mode button, ringed in amber-500
//   submit  amber-700/500    the loudest control
//   banner  bg-note-accent   solid amber-500 with near-black text — the plain-language
//                            statement of mode, and the last thing read before sending
//
// Public mode is the same structure in the cool set on an ordinary surface, so the two
// composers differ by hue, by fill, and by wording, not by luminance alone.
//
// Border width is 2 in both modes so switching mode never shifts the layout.
const MODES = {
  internal: {
    title: 'Internal note',
    subtitle: 'Staff only',
    selected: 'border-note-accent bg-note-strong text-note-ink ring-2 ring-note-accent',
    panel: 'border-2 border-note-accent bg-note',
    banner: 'bg-note-accent text-note-on-accent border-note-line',
    // amber-600 under white is 3.2:1 — under AA, on the control that commits the write.
    // amber-700 keeps the same read at 5.0:1. In dark it inverts to a filled amber-500
    // with near-black text (7.0:1) so the button still shouts off a near-black page.
    submit:
      'bg-amber-700 text-white hover:bg-amber-800 ' +
      'dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400',
    submitLabel: 'Save internal note',
  },
  public: {
    title: 'Reply to customer',
    subtitle: 'Sent by email',
    selected:
      'border-brand-600 dark:border-brand-400 bg-reply-strong text-reply-ink ' +
      'ring-2 ring-brand-600 dark:ring-brand-400',
    panel: 'border-2 border-reply-accent/50 bg-reply-strong',
    // Raised off the panel rather than tinted into it, so it still reads as a statement.
    banner: 'bg-surface text-reply-ink border-reply-accent/60',
    submit: 'bg-brand-600 text-white hover:bg-brand-700',
    submitLabel: 'Send to customer',
  },
};

function ModeButton({ mode, selected, disabled, onSelect, description }) {
  const config = MODES[mode];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`rounded-lg border-2 p-3 text-left transition-colors ${
        selected
          ? config.selected
          : 'border-line-strong bg-surface text-ink-secondary hover:bg-sunken disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:hover:bg-sunken'
      }`}
    >
      <span className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`grid size-4 place-items-center rounded-full border ${
            // border-line-strong is a hairline; the radio outline has to read as a
            // control, so the unselected ring borrows the muted ink instead.
            selected ? 'border-current' : 'border-ink-muted'
          }`}
        >
          {selected && <span className="size-2 rounded-full bg-current" />}
        </span>
        <span className="text-sm font-semibold">{config.title}</span>
      </span>
      <span className="mt-1 block text-xs">{description}</span>
    </button>
  );
}

/**
 * @param {object} props
 * @param {string} props.publicRef
 * @param {object} props.customer      The requester — named in the outbound banner.
 * @param {Array}  props.ccs           ticket.ccs; they receive customer-facing replies too.
 * @param {Array}  props.users         GET /api/users, for @mentions. Empty if that call failed.
 * @param {(message: object, options: {statusChanged: boolean}) => void} props.onPosted
 */
export default function ReplyComposer({ publicRef, customer, ccs, users, onPosted }) {
  const user = useSession();
  const canEmailCustomer = user.can_send_customer_email;
  const canWrite = user.can_be_assigned || user.can_send_customer_email;

  const [mode, setMode] = useState('internal');
  const [body, setBody] = useState('');
  const [statusChange, setStatusChange] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // The submit button is disabled while `submitting`, and that is what stops a second
  // click in practice. This ref is the same guard made SYNCHRONOUS, and it is here
  // because of what POST /tickets/:ref/messages now does: it emails the customer. The
  // route has no idempotency key (see the report's follow-up), so two requests are two
  // message rows AND two emails, and there is no undo. `submitting` is React state —
  // `handleSubmit` reads the value its render closed over, so two submit events that
  // arrive before React re-renders both see `false`. A ref is written and read in the
  // same tick, so the second one cannot. It costs three lines and removes a whole class
  // of "how did that happen" from the one screen where the mistake reaches a customer.
  const inFlight = useRef(false);

  // A viewer has neither capability, so even an internal note would be a 403. Say so
  // rather than showing a box that cannot be submitted.
  if (!canWrite) {
    return (
      <div className="rounded-lg border border-line bg-sunken px-4 py-6 text-center text-sm text-ink-secondary">
        Your account has read-only access to tickets.
      </div>
    );
  }

  const config = MODES[mode];
  const isInternal = mode === 'internal';

  // SPEC.md §7.3 asks for a confirmation step when a *collaborator* sends outward:
  // they are the group that gained customer email when the structural protection was
  // removed, and internal notes are their primary surface. Expressed as capabilities
  // rather than a role name — `can_send_customer_email` without `can_be_assigned` is
  // exactly the collaborator, and role is display-only (docs/API.md §1.8).
  const confirmBeforeSending = canEmailCustomer && !user.can_be_assigned;

  function changeMode(next) {
    setMode(next);
    setConfirming(false);
    setError(null);
    // Mentions are the internal cross-team channel; sending them on a customer-facing
    // reply is 422 MENTIONS_ON_PUBLIC_REPLY, and a customer must never receive a
    // message carrying our staff's names (docs/API.md §3.5).
    if (next === 'public') setMentionIds([]);
  }

  function toggleMention(id) {
    setMentionIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }

  async function post() {
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const payload = { is_internal_note: isInternal, body_text: body.trim() };
      if (isInternal && mentionIds.length > 0) payload.mention_user_ids = mentionIds;
      if (statusChange) payload.status = statusChange;

      const created = await api.post(`/tickets/${publicRef}/messages`, payload);

      setBody('');
      setMentionIds([]);
      setConfirming(false);
      // Every fresh compose starts internal, including the one after a customer reply.
      // The alternative — a composer that stays pointed at the customer while the agent
      // types the follow-up note about the call — is the leak this screen exists to
      // prevent, and the cost of being wrong the other way is one extra click.
      setMode('internal');
      const changedStatus = Boolean(statusChange);
      setStatusChange('');
      onPosted(created, { statusChanged: changedStatus });
    } catch (postError) {
      setError(postError);
      setConfirming(false);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!body.trim() || submitting || inFlight.current) return;
    if (!isInternal && confirmBeforeSending && !confirming) {
      setConfirming(true);
      return;
    }
    post();
  }

  const recipients = [customer, ...ccs.map((cc) => cc.customer)].filter(Boolean);
  // Mentioning yourself is a notification to yourself; drop it from the picker rather
  // than let the server write a `mentions` row nobody wanted.
  const mentionable = users.filter((candidate) => candidate.id !== user.id);

  return (
    <form onSubmit={handleSubmit} className={`rounded-lg ${config.panel} p-4`}>
      <div
        role="radiogroup"
        aria-label="Compose mode"
        className="grid gap-2 sm:grid-cols-2"
      >
        <ModeButton
          mode="internal"
          selected={isInternal}
          onSelect={() => changeMode('internal')}
          description="Visible to staff only. Never emailed."
        />
        <ModeButton
          mode="public"
          selected={!isInternal}
          disabled={!canEmailCustomer}
          onSelect={() => changeMode('public')}
          description={
            canEmailCustomer
              ? `Emailed to ${customer.name}`
              : 'Unavailable: your account cannot send customer-facing email'
          }
        />
      </div>

      {/* The mode is stated again in words, in the composer's own colours, so it is
          legible from the far side of a desk. */}
      <p className={`mt-3 rounded-md border px-3 py-2 text-sm font-medium ${config.banner}`}>
        {isInternal ? (
          <>Internal note: the customer will not see this.</>
        ) : (
          <>
            This will be emailed to{' '}
            {recipients.map((person, index) => (
              <span key={person.id}>
                {index > 0 && ', '}
                {person.name} &lt;{person.email}&gt;
              </span>
            ))}
            .
          </>
        )}
      </p>

      <label className="sr-only" htmlFor="composer-body">
        {config.title}
      </label>
      {/* No bg override on the textarea: .field-input is already bg-surface in light and
          bg-sunken in dark, which is what keeps the box from disappearing into a dark
          card. Overriding it with bg-white would reintroduce the flash it exists to fix. */}
      <textarea
        id="composer-body"
        className="field-input mt-3"
        rows={6}
        value={body}
        placeholder={isInternal ? 'Note for the team…' : `Reply to ${customer.name}…`}
        onChange={(event) => {
          setBody(event.target.value);
          setConfirming(false);
        }}
      />

      {isInternal && mentionable.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-note-ink">
            Notify teammates {mentionIds.length > 0 && `(${mentionIds.length})`}
          </summary>
          <div className="mt-2 flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
            {mentionable.map((teammate) => (
              <button
                key={teammate.id}
                type="button"
                aria-pressed={mentionIds.includes(teammate.id)}
                onClick={() => toggleMention(teammate.id)}
                className={`rounded-full px-2.5 py-1 text-xs ring-1 ring-inset ${
                  mentionIds.includes(teammate.id)
                    ? 'bg-amber-700 text-white ring-amber-800 ' +
                      'dark:bg-amber-500 dark:text-amber-950 dark:ring-amber-400'
                    : 'bg-surface text-ink-secondary ring-line-strong hover:bg-sunken'
                }`}
              >
                {teammate.display_name}
              </button>
            ))}
          </div>
        </details>
      )}

      {/* No error token in the shared vocabulary, so the error box takes an explicit dark
          step: red-50/red-800 is 7.6:1 in light, red-950/red-200 is 11.2:1 in dark. */}
      {error && (
        <div
          className="mt-3 rounded-md border px-3 py-2 text-sm
                     border-red-300 bg-red-50 text-red-800
                     dark:border-red-800 dark:bg-red-950 dark:text-red-200"
        >
          <p className="font-medium">{error.message}</p>
          {error.code === 'SENDER_CANNOT_EMAIL' && (
            <p className="mt-1 text-xs">
              Switch to an internal note, or ask an agent to reply to the customer.
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-ink-secondary">
          Then set status to
          <select
            className="field-input w-auto py-1"
            value={statusChange}
            onChange={(event) => setStatusChange(event.target.value)}
          >
            {/* The reply-and-solve path: 89.3% of tickets are one-touch, so setting the
                status in the same request is the common case, not a shortcut. */}
            <option value="">Leave unchanged</option>
            {BASE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>

        {confirming ? (
          <div className="flex items-center gap-2 rounded-md border border-brand-600 dark:border-brand-400 bg-surface px-3 py-1.5">
            <span className="text-sm text-ink">Email this to {customer.name}?</span>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setConfirming(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className={`btn ${config.submit}`} disabled={submitting}>
              {submitting ? 'Sending…' : 'Yes, send it'}
            </button>
          </div>
        ) : (
          <button
            type="submit"
            className={`btn ${config.submit}`}
            disabled={submitting || body.trim().length === 0}
          >
            {submitting ? 'Saving…' : config.submitLabel}
          </button>
        )}
      </div>
    </form>
  );
}
